import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FilterInDropdown } from '@sorare/core/src/atoms/dropdowns/FilterInDropdown';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';

import { SelectHeader } from 'atoms/SelectHeader';

type Props = {
  options?: {
    value: Currency;
    label: string;
  }[];
  selectedCurrency: Currency;
  onChange: (currency: Currency) => void;
} & Pick<
  ComponentProps<typeof FilterInDropdown>,
  'buttonColor' | 'fullWidth' | 'buttonSize' | 'blur'
>;

export const SelectCurrency = ({
  options: optionsProps,
  selectedCurrency,
  onChange,
}: Props) => {
  const {
    walletPreferences: { onlyShowFiatCurrency },
  } = useCurrentUserContext();
  const { fiatCurrency, cryptocurrencySymbol } = useCurrentUserCurrencies();

  const options = optionsProps || [
    {
      value: Currency.FIAT,
      label: fiatCurrency,
    },
    ...(!onlyShowFiatCurrency
      ? [
          {
            value: Currency.ETH,
            label: cryptocurrencySymbol,
          },
        ]
      : []),
  ];

  const selectedOption =
    options.find(o => o.value === selectedCurrency) || options[0];

  if (options.length === 1) {
    return (
      <Button size="compact" color="tertiary" disabled>
        {selectedOption.label}
      </Button>
    );
  }
  return (
    <FilterInDropdown buttonLabel={selectedOption.label} buttonSize="compact">
      {({ closeDropdown }) => (
        <>
          <SelectHeader>
            <FormattedMessage
              id="SelectCurrency.title"
              defaultMessage="Select currency"
            />
          </SelectHeader>
          <RadioGroup
            modalSelect
            options={options}
            value={selectedOption.value}
            name="currency-selector"
            onChange={currency => {
              onChange(currency);
              closeDropdown();
            }}
          />
        </>
      )}
    </FilterInDropdown>
  );
};
